/* global TPDirect */
/* eslint-disable no-unused-vars */
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button, Dialog, DialogContent, Slide, makeStyles, useMediaQuery,
  RadioGroup,
  Radio,
  withStyles,
  FormControlLabel,
  Box,
} from '@material-ui/core';
import { joiResolver } from '@hookform/resolvers/joi';
import formErrorMessage from '@/configs/formErrorMessage';
import Joi from '@hapi/joi';
import { ErrorMessage } from '@hookform/error-message';
import ProgressBackdrop from '../../../preorder/components/ProgressBackdrop';
import { PAYMENT_METHOD } from '../../../preorder/containers/CheckoutPage/components/Checkout';
import { CreditCardField } from '../../../menu/components/productDetailModal/PreOrder';
import Alert from '../../../preorder/components/Alert';
import withTapPay from '../../../hocs/withTapPay';
import VoucherShopItem from './VoucherShopItem';

const StyledRadio = withStyles(() => ({
  root: {
    '&$checked': {
      color: '#009687',
    },
  },
  checked: {},
}))(Radio);

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);
const useStyle = makeStyles((theme) => ({
  scrollPaper: {
    alignItems: 'flex-end',

    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
    },
  },
  paper: {
    height: '90%',
    fontSize: 14,
  },
  title: {
    color: '#b20000',
    fontSize: 18,
  },
  sendButton: {
    borderRadius: 8,
    flexShrink: 0,
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(2),
    padding: theme.spacing(0.5, 1),
  },
  priceContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  value: {
    fontWeight: 'bold',
  },
  price: {
    color: '#b20000',
    fontSize: 18,
    fontWeight: 'bold',
  },
  claim: {
    color: 'rgba(0,0,0,.5)',
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  expirationDate: {
    marginTop: theme.spacing(1),
  },
  countContainer: {
    display: 'flex',
    fontWeight: 'bold',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1.5),
  },
  totalPriceContainer: {
    display: 'flex',
    fontWeight: 'bold',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
  },
  checkotContainer: {
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 'bold',
    marginTop: theme.spacing(1),
  },
  hr: {
    borderTop: '1px solid #979797',
    width: '100%',
  },
  checkoutButton: {
    background: '#e08816 !important',
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
}));

const useCountTextFieldStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    display: 'flex',
  },
  input: {
    border: 0,
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    width: 50,

    '&:disabled': {
      background: 'transparent',
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  button: {
    alignItems: 'center',
    border: 0,
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    fontWeight: 'bold',
    height: 20,
    justifyContent: 'center',
    outline: 'none',
    padding: theme.spacing(0.5),
    width: 24,
  },
}));

const CountTextField = (props) => {
  const classes = useCountTextFieldStyles();
  const { onChange, value } = props || {};

  return (
    <div className={classes.root}>
      <button
        className={classes.button}
        disabled={value <= 1}
        type="button"
        onClick={() => {
          if (!parseInt(value, 10)) return;

          onChange(parseInt(value, 10) - 1);
        }}
      >
        -
      </button>
      <input
        {...props}
        disabled
        type="number"
        min={1}
        className={classes.input}
      />
      <button
        className={classes.button}
        type="button"
        onClick={() => {
          onChange(parseInt(value, 10) + 1);
        }}
      >
        +
      </button>
    </div>
  );
};

const schema = Joi.object({
  paymentMethod: Joi
    .string()
    .valid(PAYMENT_METHOD.TAP_PAY, PAYMENT_METHOD.LINE_PAY)
    .required(),
  buyCount: Joi.number().integer().min(1).max(100)
    .required(),
});

export default withTapPay(({
  data,
  isOpen,
  isLoading,
  onClose,
  onCheckoutButtonClick: propsOnCheckoutButtonClick,
}) => {
  const classes = useStyle();
  const [alert, setAlert] = React.useState(undefined);
  const { id, price } = data;
  const isUpSm = useMediaQuery((theme) => theme.breakpoints.up('sm'));
  const fullScreen = !isUpSm;
  const {
    control,
    errors,
    trigger,
    watch,
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      buyCount: 1,
      paymentMethod: PAYMENT_METHOD.TAP_PAY,
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages: formErrorMessage,
    }),
  });
  const paymentMethod = watch('paymentMethod');
  const buyCount = watch('buyCount');

  const onCheckoutButtonClick = async () => {
    const isValid = await trigger();

    if (!isValid) return;

    try {
      let cardInfo = {};

      if (paymentMethod === PAYMENT_METHOD.TAP_PAY) {
        cardInfo = await new Promise((res) => {
          TPDirect.card.getPrime(({ status = -1, card = {} }) => {
            if (status < 0) throw new Error('您輸入的信用卡資訊有錯誤，請重新輸入！');
            res(card);
          });
        });
      }

      const success = await propsOnCheckoutButtonClick({
        id,
        paymentMethod,
        buyCount,
        ...(paymentMethod === PAYMENT_METHOD.TAP_PAY && {
          paymentInfo: {
            cardNumber: `${cardInfo?.bincode}******${cardInfo?.lastfour}`,
            cardToken: cardInfo?.prime,
            cardType: cardInfo?.type,
          },
        }),
      });

      if (success) onClose();
    } catch (e) {
      setAlert({
        title: '交易失敗',
        message: e.message,
      });
    }
  };

  return (
    <Dialog
      classes={{
        scrollPaper: classes.scrollPaper,
        paper: classes.paper,
      }}
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      fullScreen={fullScreen}
      TransitionComponent={Transition}
    >
      <ProgressBackdrop isShow={isLoading} />
      <DialogContent>
        <VoucherShopItem readOnly data={data} />
        <div style={{ marginTop: 8 }}>
          以下地點不適用該優惠券：POCar店、大鵬灣店、大苑子店、白蘭氏店、厚生市集店、歪國零食嘴店、鮮乳坊店、黎記冰糖醬鴨
        </div>
        <div className={classes.claim}>
          <div>【使用須知】</div>
          <ol style={{ paddingInlineStart: 32 }}>
            <li>請於購買前確認該組優惠券之面額與張數。售出後恕不接受取消與退款。</li>
            <li>請注意，一次消費只能抵用一張優惠券。</li>
            <li>請確保於機台消費前將本券設為「最優先使用」，或於線上消費時，於結帳流程「選取優惠券」，以完成優惠折抵。</li>
            <li>本券無法兌換現金或找零，將於符合該券折抵面額時，一次抵用完畢。</li>
            <li>本券請自行妥善保管，如遭他人盜用，本券不再補發。</li>
            <li>本券所折抵之消費金額將不予開立統一發票。</li>
            <li>本券為有價證券，請勿擅自偽造、變造，以免觸犯刑責。</li>
            <li>自由食間保留商品或服務是否適用本券之權利。</li>
          </ol>
        </div>
        <div className={classes.countContainer}>
          <div>訂購數量：</div>
          <Controller
            control={control}
            name="buyCount"
            render={(props) => (
              <CountTextField
                {...props}
              />
            )}
          />
        </div>
        <ErrorMessage errors={errors} name="buyCount" as="p" style={{ color: 'red' }} />
        <div className={classes.totalPriceContainer}>
          <div>支付金額：</div>
          <div className={classes.price}>{`$${(parseInt(buyCount, 10) || 1) * (price || 0)}`}</div>
        </div>
        <div className={classes.checkotContainer}>
          <div>結帳方式：</div>
          <Controller
            control={control}
            name="paymentMethod"
            render={(props) => (
              <RadioGroup {...props}>
                <FormControlLabel value={PAYMENT_METHOD.LINE_PAY} control={<StyledRadio />} label="以 LINE Pay 付款" />
                <FormControlLabel value={PAYMENT_METHOD.TAP_PAY} control={<StyledRadio />} label="以 信用卡 付款" />
              </RadioGroup>
            )}
          />
          {paymentMethod === PAYMENT_METHOD.TAP_PAY
          && <CreditCardField />}
        </div>
        <Button
          classes={{ root: classes.checkoutButton }}
          fullWidth
          onClick={onCheckoutButtonClick}
        >
          購買

        </Button>
      </DialogContent>
      {alert && (
      <Alert
        isOpen
        title={alert?.title || ''}
        content={alert?.message || ''}
        buttons={alert?.buttons || (
          <Button fullWidth onClick={() => setAlert(undefined)}>
            <Box fontWeight="fontWeightBold" color="support.main">
              確認
            </Box>
          </Button>
        )}
      />
      )}
    </Dialog>
  );
});
